/**
 * The one chart on this screen, and the two things it refuses to draw.
 *
 * `DESIGN.md` §8 caps a dashboard at three charts above the fold and says why: "A
 * number a manager can read in one fixation beats a sparkline they have to
 * interpret." This screen ships **one**, because the two others somebody would reach
 * for — a per-department split and a productive/unproductive split — have no
 * published data source (`web/shapes/overview.md` §4.4 and §4.5), and a chart drawn
 * from an invented source is worse than no chart.
 *
 * ── A day with no rollup is a gap, not a zero-height bar ────────────────────
 *
 * They are the same picture, and they are opposite claims. So a day with no rows is
 * drawn as a labelled gap with a dotted baseline, and it is named in the caption. A
 * day carrying `partial` rows is drawn at a lighter fill **and** hatched — two
 * channels, `DESIGN.md` §4's rule, so the distinction survives the greyscale
 * screenshot `docs/UI_PROCESS.md` §4 asks for and survives a reader who cannot
 * distinguish the two fills.
 *
 * ── The chart is not the only rendering of its own data ────────────────────
 *
 * Every figure in it is also in the caption below, as a sentence per day. An `<svg>`
 * bar is not readable, and a `<title>` per bar would put thirty tab stops in the
 * middle of the page. The chart carries `role="img"` and one summary label; the
 * caption is the accessible rendering.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../../i18n';
import { formatDurationSeconds } from '../../timer/elapsed';
import type { TrendDay, WindowFold } from '../../overview/fold';
import { EmptyState } from '../../states/EmptyState';

/** Bars shorter than this are still visible as bars rather than as a hairline. */
const MIN_VISIBLE_PERCENT = 2;

function heightPercent(value: number, peak: number): number {
  if (peak <= 0 || value <= 0) return 0;
  return Math.max(MIN_VISIBLE_PERCENT, Math.round((value / peak) * 100));
}

/**
 * A day WITH data. A day without one never reaches this function — see the render,
 * where the two cases are different DOM rather than the same DOM with a different
 * class. That is deliberate: a mutation that stopped distinguishing them would
 * otherwise only change an attribute, and a test asserting the attribute would go on
 * passing while the chart drew a zero-height bar over a day nobody measured.
 */
function fillOf(day: TrendDay): string {
  return day.partial > 0 || day.missing > 0
    ? 'wp-trend__bar wp-trend__bar--partial'
    : 'wp-trend__bar';
}

export function TrendChart({
  fold,
  days,
  drillTo,
}: {
  readonly fold: WindowFold;
  readonly days: number;
  /** `/attendance` with this screen's filter context already in it. */
  readonly drillTo: string;
}): ReactNode {
  const { t } = useI18n();
  const labels = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  const withData = fold.perDay.filter((day) => day.rows > 0);

  if (withData.length < 2) {
    return (
      <section className="wp-section" data-testid="k4-trend" aria-labelledby="k4-trend-title">
        <h2 className="wp-section__title" id="k4-trend-title">
          {t('overview.trend.title', { days })}
        </h2>
        <EmptyState title={t('overview.trend.tooShort')} testId="k4-trend-too-short" />
      </section>
    );
  }

  const peak = Math.max(...fold.perDay.map((day) => day.onlineSeconds));

  return (
    <section className="wp-section" data-testid="k4-trend" aria-labelledby="k4-trend-title">
      <h2 className="wp-section__title" id="k4-trend-title">
        {t('overview.trend.title', { days })}
      </h2>
      <p className="wp-small wp-muted">{t('overview.trend.subtitle')}</p>
      <p className="wp-small wp-muted" data-testid="k4-trend-no-departments">
        {t('overview.trend.noDepartments')}
      </p>

      <div
        className="wp-trend"
        role="img"
        aria-label={t('overview.trend.axis', {
          value: formatDurationSeconds(peak, labels),
        })}
        data-testid="k4-trend-plot"
      >
        {fold.perDay.map((day) => (
          <div className="wp-trend__column" key={day.date} data-testid={`k4-trend-col-${day.date}`}>
            {day.rows === 0 ? (
              // A day the rollup has not reached. **One** element, with no height at
              // all — a dotted baseline drawn by CSS. A zero-height bar and a zero
              // are the same picture and opposite claims, so the day with no data is
              // not given a height to be zero.
              <div className="wp-trend__bar wp-trend__bar--gap" />
            ) : (
              <>
                <div
                  className={fillOf(day)}
                  style={{ height: `${String(heightPercent(day.onlineSeconds, peak))}%` }}
                />
                <div
                  className="wp-trend__bar wp-trend__bar--active"
                  style={{ height: `${String(heightPercent(day.activeSeconds, peak))}%` }}
                />
              </>
            )}
            {/* The direct label `DESIGN.md` §4 rule 3 asks for: the day under its
                own column, so the strip is readable without counting bars back to a
                legend. A `<span>`, because the column's `<div>` children are exactly
                its bars and `overview.test.tsx` counts them to tell a gap from a
                zero. */}
            <span className="wp-trend__tick" aria-hidden="true">
              {day.date.slice(8)}
            </span>
          </div>
        ))}
      </div>

      <ul className="wp-trend__caption" data-testid="k4-trend-caption">
        {fold.perDay.map((day) => (
          <li key={day.date} data-testid={`k4-trend-say-${day.date}`}>
            {day.rows === 0
              ? t('overview.trend.gap', { date: day.date })
              : t('overview.trend.caption', {
                  date: day.date,
                  online: formatDurationSeconds(day.onlineSeconds, labels),
                  active: formatDurationSeconds(day.activeSeconds, labels),
                  employees: day.employees,
                })}
            {day.partial > 0
              ? ` ${t('overview.trend.partial', { date: day.date, partial: day.partial })}`
              : ''}
          </li>
        ))}
      </ul>

      <p className="wp-small">
        <Link to={drillTo} data-testid="k4-trend-drill">
          {t('overview.trend.drill')}
        </Link>
      </p>
    </section>
  );
}
